


































import Vue from 'vue';
import Component from 'vue-class-component';
import ShowItem from '../components/ShowItem.vue';
import CreateItem from '../components/CreateItem.vue';
import ItemService from '../services/itemService';
import { Item, ItemSpec, ItemCoreSpec, AuthenticationModuleState, User } from '../types';
import { mapState } from 'vuex';
import UserService from '../services/userService';
import { Route, NavigationGuardNext } from 'vue-router';
import EditItem from '../components/EditItem.vue';

@Component({
  components: {
    ShowItem,
    CreateItem,
    EditItem
  },
  computed: {
    ...mapState<AuthenticationModuleState>('authenticationModule', {
      localUser: (state: AuthenticationModuleState) => state.localUser
    })
  }
})
export default class List extends Vue {
  public itemService: ItemService | null = null;
  public userService: UserService | null = null;
  public items: Item[] | null = null;
  public userID: string | null = null;
  public username: string | null = null;
  public editingItemID: string | null = null;

  get itemCount() {
    return (this.items) ? this.items.length : 0;
  }

  get isOwnedByLocalUser() {
    const localUser = this.$store.state.authenticationModule.localUser;
    if (!localUser) {
      throw new Error('List.isOwnedByLocalUser: localUser is null');
    }

    return localUser._id === this.userID;
  }

  public mounted(): void {
    this.userID = this.$route.params.userID;

    this.itemService = new ItemService();
    this.fetchItems().then((items: Item[]) => {
      this.items = items;
    });

    this.userService = new UserService();
    this.updateUsername().then((username: string) => {
      this.username = username;
    });
  }

  public beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext) {
    if (to.params.userID !== from.params.userID) {
      this.userID = to.params.userID;

      this.fetchItems().then((items: Item[]) => {
        this.items = items;
      });

      this.updateUsername().then((username: string) => {
        this.username = username;
      });
    }

    next();
  }

  public fetchItems(): Promise<Item[]> {
    if (!this.itemService) {
      throw new Error('List.fetchItems: itemService is null');
    }

    if (!this.userID) {
      throw new Error('List.fetchItems: userID is null');
    }

    return this.itemService.fetchItemsForUser(this.userID);
  }

  public updateUsername(): Promise<string> {
    if (!this.userService) {
      throw new Error('List.updateUsername: userService is null');
    }

    if (!this.userID) {
      throw new Error('List.fetchItems: userID is null');
    }

    return this.userService.getUserByID(this.userID)
      .then((user: User) => {
        return user.username;
      });
  }

  public onItemCreated(itemCoreSpec: ItemCoreSpec): void {
    if (!itemCoreSpec) {
      throw new Error('List.onItemCreated: itemCoreSpec is null or undefined');
    }

    const localUser = this.$store.state.authenticationModule.localUser;
    if (!localUser) {
      throw new Error('List.onItemCreated: localUser is null or undefined');
    }

    const itemSpec: ItemSpec = {
      ownerUserID: localUser._id,
      name: itemCoreSpec.name,
      description: itemCoreSpec.description,
      link: itemCoreSpec.link
    };

    if (!this.itemService) {
      throw new Error('List.onItemCreated: itemService is null');
    }

    this.itemService.createItem(itemSpec)
      .then(() => {
        this.fetchItems().then((items: Item[]) => {
          this.items = items;
        });
      })
      .catch((reason: Error) => {
        console.error(`Failed to create item. Error: ${reason}`);
      });
  }

  public onItemEditPressed(item: Item): void {
    console.log(`Editing item: ${JSON.stringify(item)}`);

    this.editingItemID = item._id;
  }

  public onItemDeleted(): void {
    this.fetchItems().then((items: Item[]) => {
      this.items = items;
    });
  }

  public onItemPurchased(): void {
    this.fetchItems().then((items: Item[]) => {
      this.items = items;
    });
  }

  public onItemUnpurchased(): void {
    this.fetchItems().then((items: Item[]) => {
      this.items = items;
    });
  }

  // @ts-ignore: We don't use item, but we may in the future, so silence unused var warning
  public onItemSaved(item: Item): void {
    this.editingItemID = null;
  }

  public onEditItemCancelPressed(): void {
    this.editingItemID = null;
  }
}
